import { selectExternalNetworkInjection } from '@/features/execute-calculation-set/mediumVoltageCalculations/selectExternalNetworkInjection';

import type {
  LoadFlowScenarioElementCalculation,
  ShortCircuitScenarioElementCalculation
} from '.';
import type { FunctionalGrid } from './grid/FunctionalGrid';
// Calculated means that busbar connected to external network is excluded - values are not calculated but injected
export function filterOnlyCalculatedMvBusbarResults<
  TResult extends (
    | LoadFlowScenarioElementCalculation
    | ShortCircuitScenarioElementCalculation
  )[]
>(results: TResult, functionalGrid: FunctionalGrid) {
  const mvBusbarIds = functionalGrid.mediumVoltageBusbars.map(b => b.objectId);

  const externalNetworkInjection =
    selectExternalNetworkInjection(functionalGrid);

  const resultsIncludingOnlyMvBusbars = results.filter(
    gridElement =>
      mvBusbarIds.includes(gridElement.id) &&
      gridElement.id !== externalNetworkInjection.objectId
  );
  return resultsIncludingOnlyMvBusbars as TResult;
}

function removeLvBusbarsFromResults<
  TResult extends (
    | LoadFlowScenarioElementCalculation
    | ShortCircuitScenarioElementCalculation
  )[]
>(results: TResult, functionalGrid: FunctionalGrid) {
  const lvBusbarIds = functionalGrid.lowVoltageBusbars.map(b => b.objectId);
  const resultsIncludingOnlyMvBusbars = results.filter(
    gridElement => !lvBusbarIds.includes(gridElement.id)
  );
  return resultsIncludingOnlyMvBusbars as TResult;
}

function removeExternalNetworkInjectionFromBusbarResults<
  TResult extends (
    | LoadFlowScenarioElementCalculation
    | ShortCircuitScenarioElementCalculation
  )[]
>(results: TResult, functionalGrid: FunctionalGrid) {
  const externalNetworkInjection =
    selectExternalNetworkInjection(functionalGrid);

  const resultsIncludingOnlyMvBusbars = results.filter(
    gridElement => gridElement.id !== externalNetworkInjection.objectId
  );
  return resultsIncludingOnlyMvBusbars as TResult;
}

export class ResultsFilter<
  TResult extends (
    | LoadFlowScenarioElementCalculation
    | ShortCircuitScenarioElementCalculation
  )[]
> {
  private constructor(
    private readonly results: TResult,
    private readonly functionalGrid: FunctionalGrid
  ) {}

  static Create<
    TResult extends (
      | LoadFlowScenarioElementCalculation
      | ShortCircuitScenarioElementCalculation
    )[]
  >(results: TResult, functionalGrid: FunctionalGrid) {
    return new ResultsFilter(results, functionalGrid);
  }

  removeLvBusbarsFromResults() {
    const newResults = removeLvBusbarsFromResults(
      this.results,
      this.functionalGrid
    );
    return new ResultsFilter(newResults, this.functionalGrid);
  }

  removeMvBusbarsFromResults() {
    const newResults = removeMvBusbarsFromResults(
      this.results,
      this.functionalGrid
    );
    return new ResultsFilter(newResults, this.functionalGrid);
  }

  removeExternalNetworkInjectionFromBusbarResults() {
    const newResults = removeExternalNetworkInjectionFromBusbarResults(
      this.results,
      this.functionalGrid
    );
    return new ResultsFilter(newResults, this.functionalGrid);
  }

  toResults() {
    return this.results;
  }
}

function removeMvBusbarsFromResults<
  TResult extends (
    | LoadFlowScenarioElementCalculation
    | ShortCircuitScenarioElementCalculation
  )[]
>(results: TResult, functionalGrid: FunctionalGrid) {
  const mvBusbarIds = functionalGrid.mediumVoltageBusbars.map(b => b.objectId);
  const resultsIncludingOnlyMvBusbars = results.filter(
    gridElement => !mvBusbarIds.includes(gridElement.id)
  );
  return resultsIncludingOnlyMvBusbars as TResult;
}
